import { LGraphNode, LiteGraph } from "litegraph.js";
import { addDrivableSlider, finalizeDrivableSliders, syncDrivableSliders } from "../utils/addDrivableSlider.js";

/**
 * NoiseNode
 * ---------
 * Generates animated value noise on an internal 512x512 canvas.
 * Uses a seeded hash for deterministic noise with bilinear interpolation.
 */
export class NoiseNode extends LGraphNode {
  constructor() {
    super();
    this.title = "Noise";

    this.properties = {
      scale: 50,
      speed: 0.5,
      octaves: 3,
      monochrome: true,
    };

    this.addOutput("Canvas", "canvas");

    this.addWidget("toggle", "Monochrome", true, (val) => {
      this.properties.monochrome = val;
    });

    addDrivableSlider(this, "Scale", "scale", 50, 1, 200, 1);
    addDrivableSlider(this, "Speed", "speed", 0.5, 0, 5, 0.1);
    addDrivableSlider(this, "Octaves", "octaves", 3, 1, 6, 1);

    finalizeDrivableSliders(this, 0);

    this._outCanvas = document.createElement("canvas");
    this._outCanvas.width = 256;
    this._outCanvas.height = 256;
    this._outCtx = this._outCanvas.getContext("2d");
    this._time = 0;
    this._frameSkip = 0;

    this.size = [200, 110];
    this.mode = LiteGraph.ALWAYS;
    this.color = "#2a2a2a";
    this.bgcolor = "#333333";
  }

  // Seeded hash function returning 0-1
  _hash(x, y, z) {
    let n = x * 374761393 + y * 668265263 + z * 1274126177;
    n = (n ^ (n >> 13)) * 1274126177;
    n = n ^ (n >> 16);
    return (n & 0x7fffffff) / 0x7fffffff;
  }

  // Smooth interpolation
  _smoothstep(t) {
    return t * t * (3 - 2 * t);
  }

  // Value noise at a point with bilinear interpolation
  _valueNoise(x, y, z) {
    const ix = Math.floor(x);
    const iy = Math.floor(y);
    const iz = Math.floor(z);
    const fx = this._smoothstep(x - ix);
    const fy = this._smoothstep(y - iy);
    const fz = this._smoothstep(z - iz);

    // Bilinear interpolation across 8 corners (trilinear with z for animation)
    const v000 = this._hash(ix, iy, iz);
    const v100 = this._hash(ix + 1, iy, iz);
    const v010 = this._hash(ix, iy + 1, iz);
    const v110 = this._hash(ix + 1, iy + 1, iz);
    const v001 = this._hash(ix, iy, iz + 1);
    const v101 = this._hash(ix + 1, iy, iz + 1);
    const v011 = this._hash(ix, iy + 1, iz + 1);
    const v111 = this._hash(ix + 1, iy + 1, iz + 1);

    const x00 = v000 + fx * (v100 - v000);
    const x10 = v010 + fx * (v110 - v010);
    const x01 = v001 + fx * (v101 - v001);
    const x11 = v011 + fx * (v111 - v011);

    const y0 = x00 + fy * (x10 - x00);
    const y1 = x01 + fy * (x11 - x01);

    return y0 + fz * (y1 - y0);
  }

  // Fractal noise with octaves
  _fbm(x, y, z, octaves) {
    let value = 0;
    let amplitude = 1;
    let totalAmplitude = 0;

    for (let i = 0; i < octaves; i++) {
      value += this._valueNoise(x, y, z) * amplitude;
      totalAmplitude += amplitude;
      x *= 2;
      y *= 2;
      z *= 2;
      amplitude *= 0.5;
    }

    return value / totalAmplitude;
  }

  onExecute() {
    syncDrivableSliders(this);

    // Skip every other frame — noise doesn't need 60fps
    this._frameSkip++;
    if (this._frameSkip % 2 !== 0) {
      this.setOutputData(0, this._outCanvas);
      return;
    }

    const { scale, speed, octaves, monochrome } = this.properties;
    const w = 256;
    const h = 256;
    const ctx = this._outCtx;
    const imageData = ctx.createImageData(w, h);
    const data = imageData.data;

    this._time += speed * 0.02;
    const invScale = 1 / Math.max(1, scale);

    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        const idx = (y * w + x) * 4;
        const nx = x * invScale;
        const ny = y * invScale;

        if (monochrome) {
          const v = Math.floor(this._fbm(nx, ny, this._time, octaves) * 255);
          data[idx] = v;
          data[idx + 1] = v;
          data[idx + 2] = v;
        } else {
          data[idx] = Math.floor(this._fbm(nx, ny, this._time, octaves) * 255);
          data[idx + 1] = Math.floor(this._fbm(nx + 100, ny + 100, this._time, octaves) * 255);
          data[idx + 2] = Math.floor(this._fbm(nx + 200, ny + 200, this._time, octaves) * 255);
        }
        data[idx + 3] = 255;
      }
    }

    ctx.putImageData(imageData, 0, 0);
    this.setOutputData(0, this._outCanvas);
  }
}

LiteGraph.registerNodeType("generators/Noise", NoiseNode);
